import { z } from "zod";

import { CellId, DataSourceTableId } from "./idTypeBrands.js";

/**
 * The few id schemas that are not simply their brand.
 *
 * Every branded id validates itself now -- `idTypeBrands.ts` applies `.uuid()`
 * to the 307 ids backed by a uuid column -- so the file of hand-written zod
 * equivalents that used to live here is gone. What is left is the handful of
 * schemas that say something the brand deliberately does not.
 */

/**
 * A branded id validated as a uuid, reached through a type parameter.
 *
 * Every brand has a named schema, so prefer naming it. This exists for the
 * cases where the brand is only available as a type argument.
 */
export const zUuidBrandedString = <BRAND extends string>(): z.ZodEffects<
  z.ZodString,
  BRAND,
  string
> =>
  z
    .string()
    .uuid()
    .transform((val) => val as BRAND);

/**
 * A `CellId` that must be a uuid, for agent tool inputs only.
 *
 * {@link CellId} itself is permissive (see its comment). A cell id the model
 * supplies is checked here instead, so a made-up id gets a validation error
 * rather than reaching a uuid column lookup, which Postgres rejects.
 */
export const zAgentInputCellId = zUuidBrandedString<CellId>();

/** {@link DataSourceTableId}, described for the agent tool schemas. */
export const zDataSourceTableId = DataSourceTableId.describe(
  "A tableId (uuid) for a table.",
);

const OUTPUT_NAME_DESCRIPTION =
  "The name of the output variable for this cell. Must be lowercase snake_case (matching `^[a-z_][a-z0-9_]*$`) and unique across all cells. When set, the cell's result is accessible as a named dataframe.";

export const zOutputName = z
  .string()
  .regex(/^[a-z_][a-z0-9_]*$/, "Must be lowercase snake_case.")
  .describe(OUTPUT_NAME_DESCRIPTION);

export const zOptionalOutputName = zOutputName
  .describe(`Optional. ${OUTPUT_NAME_DESCRIPTION}`)
  .nullish();
